// Operaciones · Torniquete · Estado
//
// Responde "¿está conectado?" y "¿qué pasó en el recinto?". Lo segundo importa tanto como
// lo primero: un equipo que figura habilitado pero no reporta eventos desde hace días
// también está caído, y así pasó un fin de semana entero sin que nadie se enterara.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Table, Tag, Space, Typography, Row, Col, Button, Modal, Alert, Empty,
  InputNumber, Select, Statistic, Divider,
} from 'antd';
import {
  ReloadOutlined, UnlockOutlined, ApiOutlined, ExclamationCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { AccesoAdmin } from '../../../../service/Acceso';
import { mensajeDeError } from '../../../../service/client';
import { useNotify } from '../../../../providers/NotificationProvider';
import TorniqueteDobles from './TorniqueteDobles';

const { Text, Paragraph } = Typography;

// Cuánto silencio del equipo se considera sospechoso. El caso real: el torniquete quedó
// sin reportar un sábado a las 15:02 y se supo el lunes.
const HORAS_SIN_EVENTOS_ALERTA = 12;

// Un rechazo no es un error del sistema: es información para el mostrador.
// `expirad` y no `expirada`: el equipo nuevo dice 'Tarjeta expirada' pero el viejo
// (10.0.0.199) dice 'Usuario expirado', y esos rechazos salían como un evento cualquiera.
const esRechazo = (evento) => /expirad|no registrad|denegad|inv[aá]lid/i.test(evento || '');

const TIPO_PERSONA = {
  inscrito: { color: 'blue', label: 'inscrito' },
  apoderado: { color: 'purple', label: 'apoderado' },
  staff: { color: 'gold', label: 'personal' },
  'sin-vigencia': { color: 'orange', label: 'sin mes al día' },
  desconocido: { color: 'default', label: 'no está en el sistema' },
};

export default function TorniqueteEstado() {
  const notify = useNotify();

  const [equipos, setEquipos] = useState(null);
  const [cargandoEquipos, setCargandoEquipos] = useState(false);
  const [reconciliacion, setReconciliacion] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [cargandoEventos, setCargandoEventos] = useState(false);
  const [puertaElegida, setPuertaElegida] = useState(null);
  const [segundos, setSegundos] = useState(5);

  const cargarEquipos = useCallback(async () => {
    setCargandoEquipos(true);
    try {
      setEquipos(await AccesoAdmin.equipos());
    } catch (e) {
      notify?.error?.(mensajeDeError(e));
    } finally {
      setCargandoEquipos(false);
    }
  }, [notify]);

  const cargarEventos = useCallback(async () => {
    setCargandoEventos(true);
    try {
      const r = await AccesoAdmin.eventos({ pageSize: 60 });
      setEventos(r?.eventos || []);
    } catch (e) {
      notify?.error?.(mensajeDeError(e));
    } finally {
      setCargandoEventos(false);
    }
  }, [notify]);

  useEffect(() => {
    cargarEquipos();
    cargarEventos();
    AccesoAdmin.reconciliacion().then(setReconciliacion).catch(() => setReconciliacion(null));
  }, [cargarEquipos, cargarEventos]);

  const abrirPuerta = () => Modal.confirm({
    title: 'Abrir el torniquete a distancia',
    icon: <ExclamationCircleOutlined />,
    okButtonProps: { danger: true },
    content: (
      <span>
        Vas a abrir <b>{puertaElegida}</b> por <b>{segundos} segundos</b>. Es una puerta
        física y cualquiera puede pasar en ese rato. Queda registrado a tu nombre.
      </span>
    ),
    okText: 'Abrir',
    cancelText: 'Cancelar',
    onOk: async () => {
      try {
        await AccesoAdmin.abrir({ puerta: puertaElegida, segundos });
        notify?.success?.(`${puertaElegida} abierta por ${segundos} s`);
        setTimeout(cargarEventos, 2000);
      } catch (e) {
        notify?.error?.(mensajeDeError(e));
      }
    },
  });

  const ultimoEvento = equipos?.ultimoEvento?.fecha ? dayjs(equipos.ultimoEvento.fecha) : null;
  const horasDeSilencio = ultimoEvento ? dayjs().diff(ultimoEvento, 'hour') : null;
  const equipoMudo = horasDeSilencio !== null && horasDeSilencio >= HORAS_SIN_EVENTOS_ALERTA;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card
        size="small"
        title={<span><ApiOutlined /> Equipos</span>}
        extra={
          <Button size="small" icon={<ReloadOutlined />} loading={cargandoEquipos} onClick={cargarEquipos}>
            Actualizar
          </Button>
        }
      >
        {equipos?.errores?.length > 0 && (
          <Alert
            type="error" showIcon style={{ marginBottom: 12 }}
            message="El servidor del torniquete no respondió del todo"
            description={equipos.errores.join(' · ')}
          />
        )}

        {equipoMudo && (
          <Alert
            type="warning" showIcon style={{ marginBottom: 12 }}
            message={`Sin eventos desde hace ${horasDeSilencio} horas`}
            description="Un equipo que no reporta eventos puede estar desconectado aunque figure habilitado. Conviene revisarlo en sitio."
          />
        )}

        <Row gutter={[12, 12]}>
          <Col xs={24} sm={8}>
            <Statistic
              title="Último evento del recinto"
              value={equipos?.ultimoEvento?.fecha || '—'}
              valueStyle={{ fontSize: 16 }}
            />
            {equipos?.ultimoEvento?.evento && <Text type="secondary">{equipos.ultimoEvento.evento}</Text>}
          </Col>
          <Col xs={24} sm={16}>
            <Space size={[8, 8]} wrap>
              {(equipos?.puertas || []).map((p) => (
                <Tag key={`${p.equipo}-${p.nombre}`} color="blue" style={{ padding: '4px 10px' }}>
                  {p.nombre}
                  {equipos?.ultimoEventoPorEquipo?.[p.equipo] && (
                    <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                      último: {equipos.ultimoEventoPorEquipo[p.equipo]}
                    </Text>
                  )}
                </Tag>
              ))}
              {!equipos?.puertas?.length && !cargandoEquipos && <Empty description="Sin puertas" image={null} />}
            </Space>
          </Col>
        </Row>

        <Divider style={{ margin: '12px 0' }} />

        <Space wrap>
          <Select
            placeholder="Puerta" style={{ minWidth: 190 }}
            value={puertaElegida} onChange={setPuertaElegida}
            options={(equipos?.puertas || []).filter(p => p.nombre).map(p => ({ value: p.nombre, label: p.nombre }))}
          />
          <InputNumber min={1} max={15} value={segundos} onChange={setSegundos} addonAfter="seg" />
          <Button danger icon={<UnlockOutlined />} disabled={!puertaElegida} onClick={abrirPuerta}>
            Abrir a distancia
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Para cuando alguien legítimo no puede entrar. Queda auditado.
          </Text>
        </Space>
      </Card>

      <TorniqueteDobles />

      <Card size="small" title="Reconciliación del equipo">
        {!reconciliacion ? (
          <Empty description="Todavía no hay ninguna corrida registrada" image={null} />
        ) : (
          <>
            <Row gutter={[16, 16]}>
              <Col xs={12} md={6}>
                <Statistic title="Fichas en el equipo" value={reconciliacion.fichas?.total ?? 0} groupSeparator="." />
              </Col>
              <Col xs={12} md={6}>
                <Statistic title="Con derecho hoy" value={reconciliacion.conDerechoHoy ?? 0} groupSeparator="." />
              </Col>
              <Col xs={12} md={6}>
                <Statistic
                  title="Staff sin acceso" value={reconciliacion.staffSinAcceso ?? 0} groupSeparator="."
                  valueStyle={{ color: reconciliacion.staffSinAcceso ? '#cf1322' : undefined }}
                />
              </Col>
              <Col xs={12} md={6}>
                <Statistic
                  title="Permanentes sin rol" value={reconciliacion.permanentesQueNoSonStaff ?? 0} groupSeparator="."
                  valueStyle={{ color: reconciliacion.permanentesQueNoSonStaff ? '#cf1322' : undefined }}
                />
              </Col>
            </Row>
            <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
              Corrida del {reconciliacion.fecha}. {reconciliacion.vigentesSinDerecho ?? 0} con ventana
              vigente sin mes pagado · {reconciliacion.huerfanasDeLaApp ?? 0} fichas que no calzan con
              ninguna inscripción · {reconciliacion.huerfanasDeOtroDepartamento ?? 0} cargadas a mano en
              el equipo (esas no se tocan) · {reconciliacion.fichasDeApoderado ?? 0} fichas de apoderado.
              Se recalcula solo una vez al día.
            </Paragraph>
          </>
        )}
      </Card>

      <Card
        size="small"
        title="Últimos eventos del recinto"
        extra={
          <Button size="small" icon={<ReloadOutlined />} loading={cargandoEventos} onClick={cargarEventos}>
            Actualizar
          </Button>
        }
      >
        <Table
          size="small"
          rowKey={(r, i) => `${r.fecha}-${i}`}
          dataSource={eventos}
          loading={cargandoEventos}
          pagination={{ pageSize: 15, showSizeChanger: false }}
          locale={{ emptyText: 'Sin eventos' }}
          columns={[
            { title: 'Fecha', dataIndex: 'fecha', width: 160 },
            {
              // El RUT lo trae la tarjeta, así que está incluso en los rechazos: es el
              // dato con el que se puede ir a buscar a la persona.
              title: 'RUT',
              dataIndex: 'rut',
              width: 110,
              render: (v) => (v ? <Text code>{v}</Text> : <Text type="secondary">—</Text>),
            },
            {
              title: 'Persona',
              render: (_, r) => (
                <Space direction="vertical" size={0}>
                  <Text>{r.nombre || <Text type="secondary">sin nombre</Text>}</Text>
                  <Space size={4} wrap>
                    <Tag color={TIPO_PERSONA[r.tipo]?.color}>{TIPO_PERSONA[r.tipo]?.label || r.tipo}</Tag>
                    {r.alDia === false && <Tag color="red">mes vencido</Tag>}
                  </Space>
                </Space>
              ),
            },
            {
              title: 'Taller',
              dataIndex: 'taller',
              ellipsis: true,
              render: (v) => v || <Text type="secondary">—</Text>,
            },
            {
              title: 'Evento',
              dataIndex: 'evento',
              render: (v) => <Tag color={esRechazo(v) ? 'red' : 'green'}>{v}</Tag>,
            },
            { title: 'Puerta', render: (_, r) => r.puerta || r.equipo || '—' },
          ]}
        />
      </Card>
    </Space>
  );
}
